import { useTranslation } from 'react-i18next'

export function LandingPage() {
  const { t, i18n } = useTranslation()

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === 'zh-TW' ? 'en' : 'zh-TW')
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-6xl mx-auto">
        <span className="text-xl font-bold text-indigo-600">履歷魔法師</span>
        <div className="flex items-center gap-4">
          <button onClick={toggleLang} className="text-sm text-gray-500 hover:text-gray-900">
            {i18n.language === 'zh-TW' ? 'EN' : '中文'}
          </button>
          <a href="#pricing" className="text-sm text-gray-600 hover:text-gray-900">
            {t('nav.pricing')}
          </a>
          <a
            href="/app"
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
          >
            {t('nav.signup')}
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-20 text-center">
        <span className="inline-block bg-indigo-50 text-indigo-700 text-sm font-medium px-3 py-1 rounded-full mb-6">
          {t('hero.badge')}
        </span>
        <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
          {t('hero.title')}
          <br />
          <span className="text-indigo-600">{t('hero.titleHighlight')}</span>
        </h1>
        <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">{t('hero.subtitle')}</p>
        <div className="flex gap-4 justify-center">
          <a
            href="/app"
            className="bg-indigo-600 text-white px-8 py-3 rounded-xl text-lg font-semibold hover:bg-indigo-700 transition-colors"
          >
            {t('hero.cta')}
          </a>
          <a
            href="#pricing"
            className="border border-gray-300 text-gray-700 px-8 py-3 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-colors"
          >
            {t('hero.ctaSecondary')}
          </a>
        </div>
        <p className="mt-6 text-sm text-gray-400">{t('hero.trustedBy')}</p>
      </section>

      {/* Features */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {t('features.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {(['format', 'ai', 'pdf', 'taiwan'] as const).map((key) => (
              <div key={key} className="bg-white rounded-2xl p-6 shadow-sm">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {t(`features.items.${key}.title`)}
                </h3>
                <p className="text-gray-600 text-sm">{t(`features.items.${key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {t('pricing.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {/* Free */}
            <div className="border border-gray-200 rounded-2xl p-8">
              <div className="text-2xl font-bold text-gray-900">{t('pricing.free.price')}</div>
              <div className="text-lg font-semibold text-gray-700 mt-1">
                {t('pricing.free.name')}
              </div>
              <p className="text-gray-500 text-sm mt-1 mb-6">{t('pricing.free.desc')}</p>
              <ul className="space-y-2 text-sm text-gray-600">
                {(t('pricing.free.features', { returnObjects: true }) as string[]).map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <span className="text-green-500">✓</span> {f}
                  </li>
                ))}
              </ul>
              <a
                href="/app"
                className="mt-8 block text-center border border-indigo-600 text-indigo-600 px-6 py-3 rounded-xl font-medium hover:bg-indigo-50 transition-colors"
              >
                {t('nav.signup')}
              </a>
            </div>
            {/* Pro */}
            <div className="bg-indigo-600 rounded-2xl p-8 text-white">
              <div className="text-2xl font-bold">
                {t('pricing.pro.price')}
                <span className="text-base font-normal opacity-80">{t('pricing.pro.period')}</span>
              </div>
              <div className="text-lg font-semibold mt-1">{t('pricing.pro.name')}</div>
              <p className="opacity-80 text-sm mt-1 mb-6">{t('pricing.pro.desc')}</p>
              <ul className="space-y-2 text-sm">
                {(t('pricing.pro.features', { returnObjects: true }) as string[]).map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <span>✓</span> {f}
                  </li>
                ))}
              </ul>
              <a
                href="/app"
                className="mt-8 block text-center bg-white text-indigo-600 px-6 py-3 rounded-xl font-medium hover:bg-indigo-50 transition-colors"
              >
                {t('nav.signup')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-sm text-gray-400">
          © 2026 {t('footer.company')} · {t('footer.rights')}
        </div>
      </footer>
    </div>
  )
}
